import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, DatePicker, InputNumber } from 'antd';
import { Button as CmsButton } from '../../../components/button';
import { FormikErrors } from 'formik';
import moment from 'moment-timezone';
import React from 'react';
import { CouponFormPeriod, LOCAL_DATE, nextPacingWeek } from './form';
import style from './style.module.scss';

interface PacingEditorProps {
    periods: CouponFormPeriod[];
    quantity: number | null;
    /** Local date the first week starts on when there is none yet. */
    firstDate: string;
    disabled: boolean;
    /** Shown once the operator has tried to save. */
    errors?: string | string[] | FormikErrors<CouponFormPeriod>[];
    onChange: (periods: CouponFormPeriod[]) => void;
}

export const PacingEditor = ({
    periods,
    quantity,
    firstDate,
    disabled,
    errors,
    onChange,
}: PacingEditorProps) => {
    const update = (index: number, change: Partial<CouponFormPeriod>) =>
        onChange(
            periods.map((period, at) =>
                at === index ? { ...period, ...change } : period
            )
        );
    const total = periods.reduce(
        (sum, period) => sum + (period.targetAwards ?? 0),
        0
    );
    const overStock = quantity !== null && total > quantity;

    return (
        <div className={style.pacing}>
            {periods.map((period, index) => {
                const week = index + 1;
                const rowErrors =
                    Array.isArray(errors) && typeof errors[index] === 'object'
                        ? (errors[index] as FormikErrors<CouponFormPeriod>)
                        : undefined;
                const rowError =
                    rowErrors &&
                    (rowErrors.periodStart ??
                        rowErrors.periodEnd ??
                        rowErrors.targetAwards);
                return (
                    <div key={index} className={style.pacing_row_wrapper}>
                        <div className={style.pacing_row}>
                            <DatePicker
                                aria-label={`Week ${week} start`}
                                showNow={false}
                                format="MMM D, YYYY"
                                allowClear={false}
                                disabled={disabled}
                                value={
                                    period.periodStart
                                        ? moment(period.periodStart, LOCAL_DATE)
                                        : null
                                }
                                onChange={(date) =>
                                    update(index, {
                                        periodStart: date
                                            ? date.format(LOCAL_DATE)
                                            : '',
                                    })
                                }
                            />
                            <DatePicker
                                aria-label={`Week ${week} end`}
                                showNow={false}
                                format="MMM D, YYYY"
                                allowClear={false}
                                disabled={disabled}
                                value={
                                    period.periodEnd
                                        ? moment(period.periodEnd, LOCAL_DATE)
                                        : null
                                }
                                onChange={(date) =>
                                    update(index, {
                                        periodEnd: date
                                            ? date.format(LOCAL_DATE)
                                            : '',
                                    })
                                }
                            />
                            <InputNumber
                                aria-label={`Week ${week} target`}
                                min={0}
                                precision={0}
                                placeholder="Target"
                                disabled={disabled}
                                value={period.targetAwards}
                                onChange={(value) =>
                                    update(index, {
                                        targetAwards:
                                            typeof value === 'number'
                                                ? value
                                                : null,
                                    })
                                }
                            />
                            {!disabled && (
                                <Button
                                    type="text"
                                    aria-label={`Remove week ${week}`}
                                    icon={<DeleteOutlined />}
                                    onClick={() =>
                                        onChange(
                                            periods.filter(
                                                (_, at) => at !== index
                                            )
                                        )
                                    }
                                />
                            )}
                        </div>
                        {rowError && (
                            <span className={style.error_text}>{rowError}</span>
                        )}
                    </div>
                );
            })}
            <div className={style.pacing_footer}>
                {!disabled && (
                    <CmsButton
                        variant="tertiary"
                        icon={<PlusOutlined />}
                        onClick={() =>
                            onChange([
                                ...periods,
                                nextPacingWeek(periods, firstDate),
                            ])
                        }
                    >
                        Add week
                    </CmsButton>
                )}
                <span
                    className={`${style.numeric} ${
                        overStock ? style.warning_text : style.muted
                    }`}
                >
                    {`Paces ${total.toLocaleString()} of ${
                        quantity === null ? '—' : quantity.toLocaleString()
                    }`}
                    {overStock && ' · more than the stock'}
                </span>
            </div>
        </div>
    );
};
